// ──────────────────────────────────────────────────────────────────────────
// Capacitor 原生桥 — 把 Capacitor 插件封装成全局 window 函数
//
// Web 模式:这个文件什么也不做(Capacitor 插件检测到不在 native 就是 no-op)
// Native(iOS/Android):这里把 ATT / SIWA / Geolocation 接通真正的原生 API
//
// 设计思路:
//   - 不修改业务代码,通过 window.__syjATT / window.__syjAppleSignIn 等钩子注入
//   - navigator.geolocation 在 native 下被替换成原生 GPS,所有现有调用自动受益
//   - 启动时隐藏 splash,设置状态栏
// ──────────────────────────────────────────────────────────────────────────
import { Capacitor, registerPlugin } from '@capacitor/core';

// 注册自定义原生插件(对应 ios/App/App/SyjAuthPlugin.swift / SyjApplePayPlugin.swift)
// registerPlugin 创建 JS 代理,native 端通过 jsName 路由到对应 .swift 文件
// 不调 registerPlugin 的话 window.Capacitor.Plugins.SyjXxx 永远 undefined
const SyjAuth = registerPlugin('SyjAuth');
const SyjApplePay = registerPlugin('SyjApplePay');

const isNative = Capacitor.isNativePlatform();
export { isNative };

// 预热 Railway 后端 — 避免首次 OTP / 注册 / 下单等请求因 Railway 冷启动超时
// 任何环境都跑(web 也有 Railway 冷启问题),不只是 native
fetch('https://syj-api-production.up.railway.app/health', { cache: 'no-cache' }).catch(()=>{});

if (isNative) {
  // ── 1. 启动后隐藏 splash + 配置状态栏 ─────────────────────────
  Promise.all([
    import('@capacitor/splash-screen'),
    import('@capacitor/status-bar'),
  ]).then(([{ SplashScreen }, { StatusBar, Style }]) => {
    // 1.5s 后淡出 splash(也可根据 React app 的 ready 事件主动调)
    setTimeout(() => SplashScreen.hide({ fadeOutDuration: 200 }).catch(()=>{}), 1500);
    // 关键修复:
    //   1. setOverlaysWebView({overlay:true}) — 让 WebView 延伸到状态栏后面,
    //      env(safe-area-inset-top) 才能正常返回真实状态栏高度
    //   2. Style.Dark = 黑色文字(状态栏在浅色背景上要用深色文字才看得见)
    //   3. setBackgroundColor 在 overlay:true 时不影响状态栏外观,但保留以防万一
    StatusBar.setOverlaysWebView({ overlay: true }).catch(()=>{});
    StatusBar.setStyle({ style: Style.Dark }).catch(()=>{});
    StatusBar.setBackgroundColor({ color: '#ffffff' }).catch(()=>{});
    // 强制显示状态栏(防止上一次 hide 之后没恢复)
    StatusBar.show().catch(()=>{});
  }).catch(e => console.warn('[native] splash/status setup failed', e));

  // ── 2. App Tracking Transparency ─────────────────────────────
  // 启动 1.5s 后由 ShoppingApp 调 window.__syjATT() 弹系统对话框
  import('capacitor-plugin-app-tracking-transparency').then(({ AppTrackingTransparency }) => {
    window.__syjATT = async () => {
      try {
        // 先查当前状态,已经决定过就不再问
        const cur = await AppTrackingTransparency.getStatus();
        if (cur?.status && cur.status !== 'notDetermined') return cur.status;
        const r = await AppTrackingTransparency.requestPermission();
        return r?.status || 'notDetermined';
      } catch (e) {
        console.warn('[ATT] failed', e);
        return 'error';
      }
    };
  }).catch(e => console.warn('[ATT] plugin not available', e));

  // ── 3. Sign in with Apple ────────────────────────────────────────────────
  // 优先用我们自己写的 SyjAuth 原生插件(ios/App/App/SyjAuthPlugin.swift)
  // 直接调 ASAuthorizationAppleIDProvider,无 URL 解析,绝不会 "Path components not found"
  // SyjAuth 由文件顶部的 registerPlugin('SyjAuth') 注入
  let usingCustomSiwa = false;
  if (SyjAuth) {
    window.__syjAppleSignIn = async () => {
      try {
        const r = await SyjAuth.appleSignIn();
        if (!r || (!r.identityToken && !r.authorizationCode)) {
          throw new Error('Apple 登录返回为空');
        }
        return {
          identityToken: r.identityToken || '',
          authorizationCode: r.authorizationCode || '',
          user: r.user || '',
          email: r.email || null,
          fullName: (r.givenName || r.familyName) ? {
            givenName: r.givenName || '',
            familyName: r.familyName || '',
          } : null,
        };
      } catch (e) {
        const msg = e?.message || String(e);
        // ASAuthorizationError code 1001 = 用户取消
        if (/code=1001/.test(msg)) throw new Error('已取消');
        throw new Error(msg);
      }
    };
    usingCustomSiwa = true;
  }

  // 如果没有原生插件,用 @capgo 兜底(web / native 没 sync 时)
  if (!usingCustomSiwa) {
    import('@capgo/capacitor-social-login').then(({ SocialLogin }) => {
      let initialized = false;
      const ensureInit = async () => {
        if (initialized) return;
        try {
          await SocialLogin.initialize({
            apple: { clientId: 'com.us198.app', redirectUrl: 'https://us198.com/auth/apple/callback' },
          });
        } catch {}
        initialized = true;
      };
      window.__syjAppleSignIn = async () => {
        await ensureInit();
        const r = await SocialLogin.login({
          provider: 'apple',
          options: { scopes: ['email', 'name'] },
        });
        const data = r?.result || r || {};
        return {
          identityToken: data.idToken || '',
          fullName: data.profile ? {
            givenName: data.profile.givenName || '',
            familyName: data.profile.familyName || '',
          } : null,
          email: data.profile?.email || null,
        };
      };
    }).catch(() => {});
  } // end !usingCustomSiwa fallback

  // ── 3.5 Apple Pay 原生插件(对应 ios/App/App/SyjApplePayPlugin.swift)──
  if (SyjApplePay) {
    window.__syjApplePay = {
      canMakePayments: async () => {
        try {
          return await SyjApplePay.canMakePayments();
        } catch {
          return { canMakePayments: false, hasCard: false };
        }
      },
      present: async (params) => SyjApplePay.presentApplePay(params),
    };
  }

  // ── 4. Geolocation — 替换 navigator.geolocation 为原生实现 ────
  // WKWebView 自带的 navigator.geolocation 在 iOS 上经常不工作 — 必须接 Capacitor
  // 原生 CoreLocation。Info.plist 已加了 NSLocationWhenInUseUsageDescription。
  //
  // 设计要点:
  //   1. 同步先放一个「队列 stub」,这样即使 dynamic import 还没 resolve,
  //      用户点按钮的请求也不会丢失 — 而是排队等真实实现就绪后批量执行
  //   2. 用 monkey-patch 覆盖 getCurrentPosition 方法(部分 iOS 版本
  //      navigator.geolocation 整体只读,但方法可写)+ 同时 try 整体替换,双保险
  //   3. requestPermissions 必须显式带 permissions: ['location'],否则某些 Capacitor
  //      版本不会弹出 iOS 系统对话框
  //   4. 明确区分 code 1(用户拒绝 — 引导去设置)/ code 2(GPS 不可用 — 重试或手填)
  (function setupGeo(){
    const queue = [];
    let realGet = null;
    const stubGet = (success, error, options) => {
      if (realGet) return realGet(success, error, options);
      queue.push([success, error, options]);
    };
    // 同步先把 stub 装上 — 监听用户的早期点击
    try { navigator.geolocation.getCurrentPosition = stubGet; } catch {}
    try {
      Object.defineProperty(navigator, 'geolocation', {
        value: {
          getCurrentPosition: stubGet,
          watchPosition: () => {},
          clearWatch: () => {},
        },
        writable: true, configurable: true,
      });
    } catch {}

    // 暴露一个全局直连 API,以防 navigator.geolocation 被某层缓存
    window.__syjGetLocation = (success, error, options) => stubGet(success, error, options);

    import('@capacitor/geolocation').then(({ Geolocation }) => {
      let permGranted = false;
      const ensurePerm = async () => {
        if (permGranted) return 'granted';
        try {
          const cur = await Geolocation.checkPermissions();
          if (cur?.location === 'granted') { permGranted = true; return 'granted'; }
          // 显式带 permissions array — 旧 Capacitor 版本不带这个不会弹对话框
          const req = await Geolocation.requestPermissions({ permissions: ['location'] });
          if (req?.location === 'granted') { permGranted = true; return 'granted'; }
          // denied / prompt-with-rationale 都视为拒绝
          return req?.location || 'denied';
        } catch {
          return 'unknown';
        }
      };

      realGet = async (success, error, options) => {
        try {
          const status = await ensurePerm();
          if (status !== 'granted' && status !== 'unknown') {
            if (error) error({
              code: 1,
              message: '定位权限未开启 — 前往「设置 → 隐私与安全 → 定位服务 → 十一家」开启',
            });
            return;
          }
          const pos = await Geolocation.getCurrentPosition({
            enableHighAccuracy: !!options?.enableHighAccuracy,
            timeout: options?.timeout || 10_000,
            maximumAge: options?.maximumAge || 0,
          });
          success({
            coords: {
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
              accuracy: pos.coords.accuracy,
              altitude: pos.coords.altitude,
              altitudeAccuracy: pos.coords.altitudeAccuracy,
              heading: pos.coords.heading,
              speed: pos.coords.speed,
            },
            timestamp: pos.timestamp,
          });
        } catch (e) {
          if (error) error({ code: 2, message: e?.message || '无法获取定位,请稍后重试' });
        }
      };

      // 真实实现就绪 → 重新覆盖 stub 并清空队列
      try { navigator.geolocation.getCurrentPosition = realGet; } catch {}
      const drained = queue.splice(0, queue.length);
      for (const [s, e, o] of drained) realGet(s, e, o);
    }).catch(e => {
      console.warn('[geolocation] plugin not available', e);
      // 失败时把队列里的请求都 reject,不让它们永远 hang 着
      for (const [, err] of queue) err?.({ code: 2, message: '定位服务初始化失败' });
      queue.length = 0;
    });
  })();

  // ── 5. App lifecycle hooks(可选,后期可加返回键/恢复同步等) ──
  import('@capacitor/app').then(({ App }) => {
    App.addListener('appStateChange', () => {
      // 切回前台时可触发数据同步(暂不做,留 hook)
    });
  }).catch(()=>{});
}
